import React, { useEffect, useState } from "react";
import {
  AllTabType,
  IOrderItems,
  IqueryParams,
  TabType,
} from "src/types/GlobalInterfaces";
import DrawerTitle from "../Drawer/DrawerTitle";
import OrdersCard from "../Cards/OrdersCard";
import { IPaginated } from "src/types/paginated";
import { RootState } from "@store/store";
import { useSelector } from "react-redux";
import httpRequest from "src/helpers/httpRequest";
import { toast } from "react-toastify";
import qs from "qs";
import { decodedToken, getLastSegment } from "src/helpers/helperFunctions";
import moment from "moment";
import NoItemUI from "../NoItemUI";
import { useNavigate } from "react-router-dom";
import NoItemImg from "src/assets/images/not-data/no-cart.png";
import TabsButton from "./TabsButton";
import TabsSlideContainer from "../GlobalComponents/Widget/TabsSlideContainer";
import InfinteScrollWrapper from "../GlobalComponents/Widget/InfinteScrollWrapper";

interface IProps {
  onBack?: () => void;
  setActiveTab?: (tab: AllTabType) => void;
  onClose?: () => void;
}

type tabTypes = "Test" | "Pharmacy";

interface iMyOrdersResponse {
  documents: IOrderItems[] | [];
  paginated: IPaginated;
}

interface IParams extends IqueryParams {
  status: tabTypes;
}

const MyOrdersScreen: React.FC<IProps> = ({
  onBack,
  setActiveTab,
  onClose,
}) => {
  const { allIndicators } = useSelector((store: RootState) => store?.redDots);

  const [isLoader, setIsLoader] = useState<boolean>(false); // Loader state for API calls
  const [allOrders, setAllOrders] = useState<IOrderItems[]>([]);
  const [paginated, setPaginated] = useState<IPaginated>({
    currentPage: 1,
    totalPages: 1,
  }); // Handles pagination state
  const [queryParams, setQueryParams] = useState<IParams>({
    page: 1,
    limit: 10,
    status: "Test",
  });

  const tabList = [
    {
      name: "Test",
      count: allIndicators?.customerOrder,
    },
    {
      name: "Pharmacy",
      count: allIndicators?.customerPharmacyOrder,
    },
  ];

  const navigate = useNavigate();

  const fetchMyOrders = async (q: IParams) => {
    const userId = decodedToken()?.id;
    if (!userId) return;
    const { status, ...remainingParams } = q;
    setIsLoader(true);
    const path =
      status === "Test"
        ? `/diagnostic-order/user/${userId}?status=&${qs.stringify(
            remainingParams
          )}`
        : status === "Pharmacy"
        ? `/pharma-order/user/${userId}?status=&${qs.stringify(
            remainingParams
          )}`
        : "";
    const { res, err } = await httpRequest<iMyOrdersResponse>({ path });
    if (res) {
      let { documents = [], paginated } = res;
      setAllOrders((prev) => [...prev, ...documents]);
      setPaginated(paginated);
    } else {
      toast(err, { type: "error" });
    }
    setIsLoader(false);
  };

  const handleChangeQueryParams = (data: Partial<IParams>, reset = false) => {
    let q: IParams = { ...queryParams, ...data };
    if (reset) {
      setAllOrders([]);
      q.page = 1;
    }
    setQueryParams(q);
    fetchMyOrders(q);
  };

  const handleTabClick = (tab: TabType) => {
    handleChangeQueryParams({ status: tab.name as tabTypes, page: 1 }, true);
  };

  const handlePageChange = (e: number) => {
    handleChangeQueryParams({ page: e });
  };

  // Fetch orders when queryParams changes
  useEffect(() => {
    handleChangeQueryParams({ page: 1, status: queryParams.status }, true);
  }, []);

  return (
    <>
      <DrawerTitle title="Orders" backBtn onBack={onBack} />
      <div className="w-full h-fit flex flex-col 2xl:p-4 p-2 2xl:gap- gap-2">
        <div className="w-full flex items-center gap-4">
          <TabsSlideContainer
            tabs={tabList}
            activeTab={
              tabList.find((tab) => tab.name === queryParams.status) ||
              tabList[0]
            }
            onChange={handleTabClick}
          />
        </div>
        <div className="w-full flex flex-col 2xl:gap-4 gap-2">
          <InfinteScrollWrapper
            isLoading={isLoader}
            length={allOrders.length}
            currentPage={queryParams.page}
            totalPages={paginated.totalPages}
            onLoadMore={handlePageChange}
            isLoadButton={true}
            noDataOptions={{
              text: "No Orders",
              text2:
                queryParams.status === "Test"
                  ? "Your test orders are empty. Explore and save tests for quick access."
                  : "Your pharmacy orders are empty. Explore and save products for quick access.",
              buttonText:
                queryParams.status === "Test"
                  ? "Discover Tests"
                  : "Discover Products",
              buttonOnPress: () => {
                navigate(
                  queryParams.status === "Test"
                    ? "/diagnostic-products"
                    : "/category"
                );
                onClose?.();
              },
              iconImagePath: NoItemImg,
            }}
            calculateHeight
            itemClassName=".tab-slider-container-main"
          >
            {allOrders.map((item, index) => {
              return (
                <OrdersCard
                  key={index}
                  id={`view-details-${index}`}
                  orderId={"#" + getLastSegment(item?.serialOrderId)}
                  status={item?.status}
                  message={item?.orderDetails
                    ?.map((v: { productName: string }) => v?.productName)
                    ?.join(" , ")}
                  date={moment(item?.createdAt).format("DD-MM-YYYY")}
                  orderPrice={item?.totalAmount || 0}
                  totleProducts={item?.orderDetails?.length}
                  onViewDetails={() => {
                    navigate(
                      `/orders/${
                        queryParams.status === "Test" ? "diagnostic" : "pharma"
                      }/${item._id}/view`
                    );
                    onClose?.();
                  }}
                  read={item?.customerNotified}
                />
              );
            })}
          </InfinteScrollWrapper>
        </div>
      </div>
    </>
  );
};

export default MyOrdersScreen;
